import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  fetchScenarios,
  fetchScenarioDefaults,
  updateAndRunScenario,
  autoProject,
  fetchDocuments,
  type ScenarioResponse,
  type AnnualCashFlow,
} from "../api/client";
import { useDealContext } from "../hooks/useDealContext";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";
import { SkeletonTable } from "../components/SkeletonLoader";
import EmptyState from "../components/EmptyState";
import AssumptionsPanel, {
  type Assumptions,
  defaultStartDate,
  buildApiPayload,
  buildDealAssumptionsPatch,
} from "../components/AssumptionsPanel";
import { updateDealAssumptions, fetchDeal } from "../api/client";
import EventTimeline from "../components/EventTimeline";
import { Table2, Download, Printer, Upload, RefreshCw, ChevronRight, ChevronDown, FileSpreadsheet, FileDown, ClipboardCheck, Clock, X, AlertTriangle, TrendingUp } from "lucide-react";
import { useToastContext } from "../contexts/ToastContext";
import { fmtCurrencyCompact, fmtCurrencyAcct, fmtPSF, fmtPercent, fmtMultiple, fmtDecimal, fmtSF, fmtDate, cleanZero } from "../utils/format";
import { downloadCSV } from "../utils/csv";
import MetricCard from "../components/MetricCard";
import SensitivityAnalysis from "../components/SensitivityAnalysis";
import SourceTracePopover from "../components/SourceTracePopover";
import { friendlyError } from "../utils/errors";

interface TenantPGIDetail {
  tenant_id: string;
  tenant_name: string;
  suite_id: string | null;
  leased_sf: number;
  base_rent_psf: number;
  rent_step_applied: string | null;
  annual_contribution: number;
  is_rollover?: boolean;
  contract_rent_psf?: number | null;
  market_rent_psf?: number | null;
  renewal_probability?: number | null;
  weighted_rent_psf?: number | null;
}

interface ReimbursementLineItem {
  category: string;
  amount: number;
  expense_amount: number;
  recovery_pct: number;
}

interface TenantReimbursementSummary {
  tenant_id: string;
  tenant_name: string;
  leased_sf: number;
  cam_reimbursement: number;
  tax_reimbursement: number;
  insurance_reimbursement: number;
  management_fee_reimbursement: number;
  total_reimbursement: number;
  cam_line_items: ReimbursementLineItem[];
  tax_line_items: ReimbursementLineItem[];
  insurance_line_items: ReimbursementLineItem[];
}

interface LineItem {
  label: string;
  key: keyof AnnualCashFlow | null;
  bold?: boolean;
  indent?: boolean;
  separator?: boolean;
  negate?: boolean;
  /** If true, this line is a deduction — never show in red */
  isDeduction?: boolean;
  /** If true, this row is expandable (PGI drill-down) */
  expandable?: boolean;
  /** Expandable group identifier for opex/reimbursement drill-down */
  expandableGroup?: "opex" | "reimbursement";
}

const lineItems: LineItem[] = [
  { label: "Potential Gross Income (Base Rent)", key: "potential_gross_income", expandable: true },
  { label: "", key: null, separator: true },
  { label: "Absorption / Turnover Vacancy / Free Rent", key: "absorption_and_turnover_vacancy", indent: true, negate: true, isDeduction: true },
  { label: "General Vacancy", key: "general_vacancy", indent: true, negate: true, isDeduction: true },
  { label: "Credit Loss", key: "credit_loss", indent: true, negate: true, isDeduction: true },
  { label: "", key: null, separator: true },
  { label: "Reimbursement Income", key: "total_reimbursement_income", indent: true, expandableGroup: "reimbursement" },
  { label: "Other Income", key: "other_income", indent: true },
  { label: "Effective Gross Income", key: "effective_gross_income", bold: true },
  { label: "", key: null, separator: true },
  { label: "Total Operating Expenses", key: "total_operating_expenses", negate: true, isDeduction: true, expandableGroup: "opex" },
  { label: "", key: null, separator: true },
  { label: "Net Operating Income", key: "net_operating_income", bold: true },
  { label: "", key: null, separator: true },
  { label: "Tenant Improvements", key: "tenant_improvements", indent: true, negate: true, isDeduction: true },
  { label: "Leasing Commissions", key: "leasing_commissions", indent: true, negate: true, isDeduction: true },
  { label: "Capital Expenditures", key: "capital_expenditures", indent: true, negate: true, isDeduction: true },
  { label: "Capital Reserves", key: "capital_reserves", indent: true, negate: true, isDeduction: true },
  { label: "Cash Flow Before Debt Service", key: "cash_flow_before_debt", bold: true },
  { label: "", key: null, separator: true },
  { label: "Debt Service", key: "debt_service", negate: true, isDeduction: true },
  { label: "Cash Flow After Debt Service", key: "cash_flow_after_debt", bold: true },
];

/** Map cash flow line-item keys to their assumption override keys. */
const OVERRIDE_KEY_MAP: Record<string, string> = {
  general_vacancy: "vacancy_rate_pct",
  credit_loss: "credit_loss_pct",
  capital_reserves: "capital_reserves_psf",
};

type PeriodMode = "annual" | "quarterly" | "monthly";
type ViewMode = "gross" | "psf";

/** Compute the year-ending date label for a projection year. */
function yearEndingLabel(startDateStr: string, yearNum: number): string {
  const [y, m, d] = startDateStr.split("-").map(Number);
  const anniversary = new Date(y + yearNum, m - 1, d);
  if (anniversary.getMonth() !== (m - 1) % 12) {
    anniversary.setDate(0);
  }
  anniversary.setDate(anniversary.getDate() - 1);
  return fmtDateLabel(anniversary);
}

/** Compute period-ending date for quarterly/monthly views.
 *  @param startDateStr Analysis start date (YYYY-MM-DD)
 *  @param yearNum 1-indexed projection year
 *  @param periodInYear 1-indexed period within the year (1-4 for quarterly, 1-12 for monthly)
 *  @param periodsPerYear 4 for quarterly, 12 for monthly
 */
function periodEndingLabel(startDateStr: string, yearNum: number, periodInYear: number, periodsPerYear: number): string {
  const [y, m] = startDateStr.split("-").map(Number);
  // Start month of projection year (0-indexed)
  const yearStartMonth = (m - 1) + (yearNum - 1) * 12;
  const monthsPerPeriod = 12 / periodsPerYear;
  const endMonth = yearStartMonth + periodInYear * monthsPerPeriod;
  // End date = last day of the ending month
  const endDate = new Date(y, endMonth, 0); // day 0 of next month = last day of target month
  return fmtDateLabel(endDate);
}

function fmtDateLabel(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return fmtDate(`${d.getFullYear()}-${mm}-${dd}`);
}

/** Map a line item key to the appropriate audit tab name. */
const AUDIT_TAB_MAP: Record<string, string> = {
  potential_gross_income: "rent-schedule",
  total_reimbursement_income: "reimbursements",
  total_operating_expenses: "expenses",
  net_operating_income: "expenses",
};

export default function CashFlowPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const { deal, setDeal, scenarios: ctxScenarios, setScenarios: setCtxScenarios } = useDealContext();
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.is_admin ?? false;
  const navigate = useNavigate();
  const { addToast } = useToastContext();
  const [localScenarios, setLocalScenarios] = useState<ScenarioResponse[]>([]);
  const [dismissedPriceBanner, setDismissedPriceBanner] = useState(false);
  // Use context scenarios if available (updated by chat), fall back to local
  const scenarios = ctxScenarios && ctxScenarios.length > 0 ? ctxScenarios : localScenarios;
  const setScenarios = (s: ScenarioResponse[]) => {
    setLocalScenarios(s);
    setCtxScenarios?.(s);
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>("");
  const [recalculating, setRecalculating] = useState(false);
  const [showEventTimeline, setShowEventTimeline] = useState(false);
  const [assumptionsDirty, setAssumptionsDirty] = useState(false);

  // View controls
  const [periodMode, setPeriodMode] = useState<PeriodMode>("annual");
  const [durationYears, setDurationYears] = useState<number | null>(null);
  const [durationStr, setDurationStr] = useState("10");
  const [viewMode, setViewMode] = useState<ViewMode>("gross");

  // Source trace popover
  const [traceCell, setTraceCell] = useState<{ label: string; value: string; year: number } | null>(null);

  // PGI drill-down
  const [pgiExpanded, setPgiExpanded] = useState(false);
  // Opex / Reimbursement drill-down
  const [opexExpanded, setOpexExpanded] = useState(false);
  const [expandedOpexGroups, setExpandedOpexGroups] = useState<Set<string>>(new Set());
  const toggleOpexGroup = (key: string) => {
    setExpandedOpexGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const [reimbExpanded, setReimbExpanded] = useState(false);
  const [expandedReimbPools, setExpandedReimbPools] = useState<Set<string>>(new Set());
  const [expandedReimbTenants, setExpandedReimbTenants] = useState<Set<string>>(new Set());
  const toggleReimbPool = (pool: string) => {
    setExpandedReimbPools(prev => {
      const next = new Set(prev);
      next.has(pool) ? next.delete(pool) : next.add(pool);
      return next;
    });
  };
  const toggleReimbTenant = (key: string) => {
    setExpandedReimbTenants(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Cmd+S to recalculate — use ref to avoid declaration-order dependency
  const recalcRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        recalcRef.current?.();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Sync assumptions when scenario changes externally (e.g., via chat)
  const activeCtxScenario = scenarios?.find((s: any) => s.id === selectedId);
  useEffect(() => {
    if (!activeCtxScenario?.assumptions) return;
    const sa = activeCtxScenario.assumptions as Record<string, unknown>;
    const sra = (sa.rollover_assumptions || {}) as Record<string, number>;
    setAssumptions(prev => ({
      ...prev,
      vacancy_rate_pct: (sa.vacancy_rate_pct as number) ?? prev.vacancy_rate_pct,
      credit_loss_pct: (sa.credit_loss_pct as number) ?? prev.credit_loss_pct,
      expense_growth_rate_pct: (sa.expense_growth_rate_pct as number) ?? prev.expense_growth_rate_pct,
      market_rent_growth_pct_annual: (sa.market_rent_growth_pct_annual as number) ?? prev.market_rent_growth_pct_annual,
      // Every other scenario-level field the panel writes back must sync
      // too, or the next "Update Pro Forma" reverts chat-set values to
      // whatever stale value the panel was still holding.
      exit_cap_rate: (sa.exit_cap_rate as number) ?? prev.exit_cap_rate,
      discount_rate: (sa.discount_rate as number) ?? prev.discount_rate,
      selling_costs_pct: (sa.selling_costs_pct as number) ?? prev.selling_costs_pct,
      capital_reserves_psf: (sa.capital_reserves_psf as number) ?? prev.capital_reserves_psf,
      expense_growth_rates: (sa.expense_growth_rates as Record<string, number>) ?? prev.expense_growth_rates,
      renewal_probability_pct: sra.renewal_probability_pct ?? prev.renewal_probability_pct,
      downtime_months: sra.downtime_months ?? prev.downtime_months,
      lease_term_years: sra.lease_term_years ?? prev.lease_term_years,
      new_tenant_ti_psf: sra.new_tenant_ti_psf ?? prev.new_tenant_ti_psf,
      renewal_tenant_ti_psf: sra.renewal_tenant_ti_psf ?? prev.renewal_tenant_ti_psf,
      // analysis_start_date intentionally not synced from scenario
      // assumptions — the deal-level date is the source of truth and stale
      // scenario dates must not be echoed back into the deal patch.
    }));
  }, [activeCtxScenario?.assumptions]);

  // ARGUS export

  // Assumptions
  const [assumptions, setAssumptions] = useState<Assumptions>({
    vacancy_rate_pct: 0.05,
    expense_growth_rate_pct: 0.03,
    going_in_cap_rate: 0.0625,
    exit_cap_rate: 0.065,
    discount_rate: 0.08,
    market_rent_growth_pct_annual: 0.03,
    capital_reserves_psf: 0.25,
    credit_loss_pct: 0.01,
    selling_costs_pct: 0.015,
    renewal_probability_pct: 0.70,
    downtime_months: 6,
    lease_term_years: 5,
    new_tenant_ti_psf: 35,
    renewal_tenant_ti_psf: 10,
    analysis_start_date: defaultStartDate(),
    purchase_price: 0,
    hold_period_years: 10,
    loan_amount: 0,
    interest_rate: 0,
    amortization_years: 30,
    io_period_years: 0,
  });

  const handleAssumptionsChange = useCallback((a: Assumptions) => {
    setAssumptions(a);
    setAssumptionsDirty(true);
  }, []);

  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;

    async function load() {
      try {
        let scenarioList = await fetchScenarios(dealId!);
        if (cancelled) return;

        // Auto-project if no projections exist
        const hasProjection = scenarioList.some((s) => s.projection_result);
        if (!hasProjection) {
          const docs = await fetchDocuments(dealId!);
          const hasRR = docs.some((d) => d.file_type === "rent_roll" && d.parse_status === "complete");
          if (hasRR) {
            try {
              await autoProject(dealId!);
              scenarioList = await fetchScenarios(dealId!);
            } catch (e: any) {
              const detail = friendlyError(e?.response?.data?.detail || e?.message || "Couldn't auto-project the cash flow.");
              console.error("Auto-project failed:", detail);
            }
          }
        }

        if (cancelled) return;
        setScenarios(scenarioList);
        const baseCase = scenarioList.find((sc) => sc.name === "Base Case" && sc.projection_result);
        const withResult = baseCase || scenarioList.find((sc) => sc.projection_result);
        if (withResult) setSelectedId(withResult.id);

        // Load defaults for assumptions panel
        try {
          const defaults = await fetchScenarioDefaults(dealId!);
          if (!cancelled) {
            const prop = deal?.property_data;
            const debt = prop?.debt as Record<string, number> | undefined;
            const scenarioAssumptions = (withResult?.assumptions || {}) as Record<string, unknown>;
            // Every scenario-level field must hydrate from the scenario FIRST.
            // The panel writes its full state back on "Update Pro Forma", so
            // hydrating from deal-level or hardcoded defaults silently reverts
            // chat-set scenario values (the "chat said 7% but it's back to 8%"
            // bug). Rollover fields read scenario → Default profile → defaults.
            const sra = (scenarioAssumptions.rollover_assumptions || {}) as Record<string, number>;
            const defaultProfile = (((prop?.market_leasing_profiles as unknown) as Record<string, number>[]) || [])[0] || {};
            const a: Assumptions = {
              vacancy_rate_pct: (scenarioAssumptions.vacancy_rate_pct as number) ?? defaults.assumptions.vacancy_rate_pct ?? 0.05,
              expense_growth_rate_pct: (scenarioAssumptions.expense_growth_rate_pct as number) ?? defaults.assumptions.expense_growth_rate_pct ?? 0.03,
              going_in_cap_rate: prop?.going_in_cap_rate ?? defaults.assumptions.going_in_cap_rate ?? 0.0625,
              // Prompt 74 moved exit_cap_rate from deal.property_data to
              // scenario.assumptions, so the scenario value is the source of
              // truth once set. Deal-level value is only a fallback.
              exit_cap_rate:
                ((scenarioAssumptions.exit_cap_rate as number) && (scenarioAssumptions.exit_cap_rate as number) > 0
                  ? (scenarioAssumptions.exit_cap_rate as number)
                  : (prop?.exit_cap_rate && prop.exit_cap_rate > 0)
                    ? prop.exit_cap_rate
                    : (defaults.assumptions.exit_cap_rate || 0.065)),
              discount_rate: (scenarioAssumptions.discount_rate as number) ?? prop?.discount_rate ?? defaults.assumptions.discount_rate ?? 0.08,
              market_rent_growth_pct_annual: (scenarioAssumptions.market_rent_growth_pct_annual as number) ?? defaults.assumptions.market_rent_growth_pct_annual ?? 0.03,
              capital_reserves_psf: (scenarioAssumptions.capital_reserves_psf as number) ?? defaults.assumptions.capital_reserves_psf ?? 0.25,
              credit_loss_pct: (scenarioAssumptions.credit_loss_pct as number) ?? defaults.assumptions.credit_loss_pct ?? 0.01,
              selling_costs_pct: (scenarioAssumptions.selling_costs_pct as number) ?? prop?.selling_costs_pct ?? defaults.assumptions.selling_costs_pct ?? 0.015,
              renewal_probability_pct: sra.renewal_probability_pct ?? defaultProfile.renewal_probability_pct ?? defaults.assumptions.renewal_probability_pct ?? 0.70,
              downtime_months: sra.downtime_months ?? defaultProfile.downtime_months ?? defaults.assumptions.downtime_months ?? 6,
              lease_term_years: sra.lease_term_years ?? defaultProfile.lease_term_years ?? defaults.assumptions.lease_term_years ?? 5,
              new_tenant_ti_psf: sra.new_tenant_ti_psf ?? defaultProfile.new_tenant_ti_psf ?? defaults.assumptions.new_tenant_ti_psf ?? 35,
              renewal_tenant_ti_psf: sra.renewal_tenant_ti_psf ?? defaultProfile.renewal_tenant_ti_psf ?? defaults.assumptions.renewal_tenant_ti_psf ?? 10,
              analysis_start_date: prop?.analysis_start_date || defaultStartDate(),
              purchase_price: prop?.purchase_price ?? 0,
              hold_period_years: prop?.hold_period_years ?? 10,
              loan_amount: debt?.loan_amount ?? 0,
              interest_rate: debt?.interest_rate ?? 0,
              amortization_years: debt?.amortization_years ?? 30,
              io_period_years: debt?.io_period_years ?? 0,
              expense_growth_rates: (scenarioAssumptions.expense_growth_rates as Record<string, number>) ?? undefined,
            };
            setAssumptions(a);
          }
        } catch { /* use defaults */ }

        // Use hold period from property_data for duration
        const hp = deal?.property_data?.hold_period_years;
        if (hp && !cancelled) {
          setDurationYears(hp);
          setDurationStr(String(hp));
        }

        // Sync purchase price from deal context
        const pp = deal?.property_data?.purchase_price;
        if (pp != null && pp > 0 && !cancelled) {
          setAssumptions(prev => prev.purchase_price === 0 ? { ...prev, purchase_price: pp } : prev);
        }
      } catch (e) {
        if (!cancelled) setError(friendlyError(e instanceof Error ? e.message : "Failed to load cash flow data"));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [dealId, deal?.property_data?.hold_period_years, deal?.property_data?.analysis_start_date]);

  // --- Recalculate projection (triggered by "Update Pro Forma" button) ---
  const handleRecalculate = useCallback(async (a: Assumptions) => {
    if (!dealId || !selectedId) return;
    setRecalculating(true);
    setError(null);
    try {
      // Persist deal-level valuation assumptions (purchase_price, debt, etc.)
      const dealPatch = buildDealAssumptionsPatch(a);
      await updateDealAssumptions(dealId, dealPatch);
      // Refresh deal context so dashboard picks up new values
      const freshDeal = await fetchDeal(dealId);
      setDeal(freshDeal);
      // Run scenario projection with updated assumptions. The backend
      // REPLACES scenario.assumptions wholesale, so merge the panel payload
      // over the current scenario assumptions — otherwise chat-set fields
      // the panel doesn't manage (tax/insurance growth, rollover LCs, free
      // rent, market rent PSF, vacancy_net_out_enabled, ...) are wiped on
      // every "Update Pro Forma" click.
      const currentSA = (scenarios?.find((s: any) => s.id === selectedId)?.assumptions ?? {}) as Record<string, unknown>;
      const panel = buildApiPayload(a);
      const payload: Record<string, unknown> = {
        ...currentSA,
        ...panel,
        rollover_assumptions: {
          ...((currentSA.rollover_assumptions as Record<string, unknown>) ?? {}),
          ...((panel.rollover_assumptions as Record<string, unknown>) ?? {}),
        },
      };
      // The panel omits debt when the loan is zeroed out — removing debt
      // must also clear the scenario-level debt or it masks the removal.
      if (!("debt" in panel)) delete payload.debt;
      await updateAndRunScenario(dealId, selectedId, { assumptions: payload });
      const updated = await fetchScenarios(dealId);
      setScenarios(updated);
      setAssumptionsDirty(false);
    } catch (e: any) {
      const detail = friendlyError(e?.response?.data?.detail || (e instanceof Error ? e.message : "Couldn't recalculate the pro forma."));
      addToast(detail, "error");
    } finally {
      setRecalculating(false);
    }
  }, [dealId, selectedId, setDeal, scenarios]);

  // Keep Cmd+S ref in sync with latest assumptions + handler
  recalcRef.current = () => { if (!recalculating) handleRecalculate(assumptions); };

  if (loading) return <SkeletonTable rows={15} cols={11} />;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <p className="text-red-700 font-medium mb-2">Failed to load cash flow data</p>
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  const scenario = scenarios.find((s) => s.id === selectedId);
  const allCfs = scenario?.projection_result?.annual_cash_flows;

  if (deal.property_data && (!deal.property_data.tenants || (deal.property_data.tenants as unknown[]).length === 0)) {
    return (
      <EmptyState
        icon={Table2}
        title="No tenants found"
        description="No tenants found in rent roll. Upload a rent roll with tenant data to generate projections."
        action={
          <Link
            to={`/deals/${dealId}/documents`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all duration-200 shadow-md"
          >
            <Upload className="w-4 h-4" />
            Upload Documents
          </Link>
        }
      />
    );
  }

  if (!allCfs || allCfs.length === 0) {
    return (
      <EmptyState
        icon={Table2}
        title="No cash flow data"
        description="Upload a rent roll and operating statement to auto-generate projections."
        action={
          <Link
            to={`/deals/${dealId}/documents`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all duration-200 shadow-md"
          >
            <Upload className="w-4 h-4" />
            Upload Documents
          </Link>
        }
      />
    );
  }

  // Apply duration filter
  const maxYears = durationYears ?? allCfs.length;
  const cfs = allCfs.slice(0, maxYears);
  const tla = deal.property_data?.total_leasable_area ?? 1;
  const isPSF = viewMode === "psf";

  // Format value based on view mode
  const fmtVal = (val: number) => {
    const clean = cleanZero(val);
    if (isPSF) return fmtPSF(clean / tla);
    return fmtCurrencyAcct(clean);
  };

  /** Format using tenant SF for per-tenant PSF (Level 2/3 reimbursement rows). */
  const fmtValTenant = (val: number, tenantSF: number) => {
    const clean = cleanZero(val);
    if (isPSF) return fmtPSF(tenantSF > 0 ? clean / tenantSF : 0);
    return fmtCurrencyAcct(clean);
  };

  // Period transformation helpers
  const effectiveStartDate = assumptions.analysis_start_date || deal?.property_data?.analysis_start_date || defaultStartDate();

  // Build In-Place column data from projection result.
  // Vacancy, credit loss, and other income come from the engine's
  // in_place_breakdown so the column reconciles to the going-in cap rate:
  // (PGI - Vacancy - Credit Loss + Other Income + Reimbursements - OpEx) == NOI.
  const inPlaceData = (() => {
    const pr = scenario?.projection_result as any;
    // Null/undefined check (not falsy): a legitimate $0 in-place NOI should
    // still render the In-Place column.
    if (pr?.in_place_noi == null || !pr?.in_place_tenant_details) return null;
    const details = pr.in_place_tenant_details as any[];
    const bd = pr.in_place_breakdown as Record<string, number> | undefined;
    const ipPGI = bd?.potential_gross_income
      ?? details.reduce((s: number, d: any) => s + (d.annual_rent ?? 0), 0);
    const ipReimb = bd?.total_reimbursement_income
      ?? details.reduce((s: number, d: any) => s + (d.reimbursement ?? 0), 0);
    const ipOpex = bd?.total_operating_expenses
      ?? details.reduce((s: number, d: any) => s + (d.expense_allocation ?? 0), 0);
    const ipVacancy = bd?.general_vacancy ?? 0;
    const ipCreditLoss = bd?.credit_loss ?? 0;
    const ipOtherIncome = bd?.other_income ?? 0;
    const ipEGI = bd?.effective_gross_income ?? (ipPGI - ipVacancy - ipCreditLoss + ipOtherIncome + ipReimb);
    const ipNOI = bd?.net_operating_income ?? (pr.in_place_noi as number);
    return {
      potential_gross_income: ipPGI,
      total_reimbursement_income: ipReimb,
      effective_gross_income: ipEGI,
      total_operating_expenses: ipOpex,
      general_vacancy: ipVacancy,
      credit_loss: ipCreditLoss,
      other_income: ipOtherIncome,
      net_operating_income: ipNOI,
      // Items not applicable to a day-1 snapshot
      absorption_and_turnover_vacancy: 0,
      tenant_improvements: 0, leasing_commissions: 0,
      capital_expenditures: 0, capital_reserves: 0,
      cash_flow_before_debt: ipNOI,
      debt_service: 0, cash_flow_after_debt: ipNOI,
    } as Record<string, number>;
  })();

  // In-Place tenant details for drill-down rendering
  const inPlaceTenantDetails: any[] = (() => {
    const pr = scenario?.projection_result as any;
    return pr?.in_place_tenant_details ?? [];
  })();

  function buildColumns(): { label: string; subLabel?: string; cfs: AnnualCashFlow[]; inPlace?: Record<string, number>; inPlaceTenants?: any[] }[] {
    if (periodMode === "annual") {
      const yearCols = cfs.map((cf) => ({
        label: `Year ${cf.year}`,
        subLabel: `YE ${yearEndingLabel(effectiveStartDate, cf.year)}`,
        cfs: [cf],
      }));
      if (inPlaceData) {
        return [{ label: "In-Place", cfs: [], inPlace: inPlaceData, inPlaceTenants: inPlaceTenantDetails }, ...yearCols];
      }
      return yearCols;
    }
    if (periodMode === "quarterly") {
      const cols: { label: string; subLabel?: string; cfs: AnnualCashFlow[] }[] = [];
      for (const cf of cfs) {
        for (let q = 1; q <= 4; q++) {
          cols.push({ label: `Q${q}`, subLabel: `QE ${periodEndingLabel(effectiveStartDate, cf.year, q, 4)}`, cfs: [cf] });
        }
      }
      return cols;
    }
    const cols: { label: string; subLabel?: string; cfs: AnnualCashFlow[] }[] = [];
    for (const cf of cfs) {
      for (let m = 1; m <= 12; m++) {
        cols.push({ label: `M${m}`, subLabel: `ME ${periodEndingLabel(effectiveStartDate, cf.year, m, 12)}`, cfs: [cf] });
      }
    }
    return cols;
  }

  const divisor = periodMode === "quarterly" ? 4 : periodMode === "monthly" ? 12 : 1;
  const columns = buildColumns();

  function getColumnValue(col: { cfs: AnnualCashFlow[]; inPlace?: Record<string, number> }, item: LineItem): number {
    if (!item.key) return 0;
    if (col.inPlace) {
      const raw = col.inPlace[item.key] ?? 0;
      return item.negate && raw > 0 ? -raw : raw;
    }
    const cf = col.cfs[0];
    if (!cf) return 0;
    const raw = (cf[item.key] as number) / divisor;
    return item.negate && raw > 0 ? -raw : raw;
  }

  /** Determine text color for a cell value.
   *  Red ONLY for negative NOI or negative cash flow after debt service. */
  function cellColor(val: number, item: LineItem): string {
    const clean = cleanZero(val);
    const isNegativeNOI = item.key === "net_operating_income" && clean < 0;
    const isNegativeCFADS = item.key === "cash_flow_after_debt" && clean < 0;
    if (isNegativeNOI || isNegativeCFADS) return "text-red-600 font-semibold";
    if (item.bold) return "font-semibold text-bark-700";
    if (item.isDeduction || item.indent) return "text-bark-500";
    return "text-bark-600";
  }

  function exportCSV() {
    if (!cfs) return;

    // Build assumptions metadata header
    const assumptionRows: (string | number)[] [] = [
      ["Property", deal.name || ""],
      ["Scenario", scenario?.name || ""],
      ["Analysis Start", assumptions.analysis_start_date || ""],
      ["Hold Period", `${assumptions.hold_period_years} years`],
      ["General Vacancy %", fmtPercent(assumptions.vacancy_rate_pct ?? 0, 1)],
      ["Expense Growth %", fmtPercent(assumptions.expense_growth_rate_pct ?? 0, 1)],
      ["Exit Cap Rate %", fmtPercent(assumptions.exit_cap_rate ?? 0, 2)],
      ["Discount Rate %", fmtPercent(assumptions.discount_rate ?? 0, 2)],
      ["Purchase Price", assumptions.purchase_price],
      ["Loan Amount", assumptions.loan_amount],
      ["Interest Rate %", fmtPercent(assumptions.interest_rate ?? 0, 2)],
      ["---"],
    ];

    const headers = [
      "Line Item",
      ...columns.map((c) => `${c.label} ${c.subLabel || ""}`),
    ];
    const dataRows = lineItems
      .filter((item) => !item.separator && item.key)
      .map((item) => {
        const row: (string | number)[] = [item.indent ? `  ${item.label}` : item.label];
        columns.forEach((col) => {
          row.push(Math.round(cleanZero(getColumnValue(col, item))));
        });
        return row;
      });
    downloadCSV(
      [...assumptionRows, headers, ...dataRows],
      `${deal.name || "pro-forma"}_pro_forma_${scenario?.name || ""}.csv`
    );
  }

  const handleDurationChange = (val: string) => {
    setDurationStr(val);
    const n = parseInt(val, 10);
    if (!isNaN(n) && n >= 1 && n <= 30) {
      setDurationYears(n);
    }
  };

  const handleDurationBlur = () => {
    const n = parseInt(durationStr, 10);
    if (isNaN(n) || n < 1) {
      setDurationStr("1");
      setDurationYears(1);
      addToast("Hold period adjusted to valid range (1-30 years)", "info");
    } else if (n > 30) {
      setDurationStr("30");
      setDurationYears(30);
      addToast("Hold period adjusted to valid range (1-30 years)", "info");
    } else {
      setDurationStr(String(n));
      setDurationYears(n);
    }
  };

  // Get tenant PGI details for a year (from the annual_cash_flows data)
  function getTenantPGIDetails(cf: AnnualCashFlow | undefined): TenantPGIDetail[] {
    if (!cf) return [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const details = (cf as any).tenant_pgi_details;
    if (Array.isArray(details)) return details;
    return [];
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Print header */}
      <div className="print-header hidden print:block">
        <h1>{deal.name || "Pro Forma"}</h1>
        <p>{deal.address || ""} &middot; {scenario?.name || "Base Case"} Pro Forma</p>
      </div>

      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-bark-800">Pro Forma</h2>
          <p className="text-sm text-bark-400 mt-1">Multi-year projection with line-item detail</p>
        </div>
        <Link
          to={`/deals/${dealId}/audit`}
          className="print:hidden shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-forest-700 bg-forest-50 border border-forest-200 rounded-lg hover:bg-forest-100 transition-all"
        >
          Calculation Audit
        </Link>
      </div>

      {/* GLA=0 warning */}
      {deal.property_data && (!deal.property_data.total_leasable_area || deal.property_data.total_leasable_area <= 0) && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 print:hidden">
          <p className="text-sm text-red-700 font-semibold">
            Total Leasable Area (GLA) is zero or missing.
          </p>
          <p className="text-sm text-red-600 mt-1">
            Pro-rata shares and reimbursements cannot be calculated without a valid GLA. Set the property&apos;s Total Leasable Area to fix this.
          </p>
        </div>
      )}

      {/* Reimbursement notice — only show when all tenants are gross or pending */}
      {(() => {
        const tenants = deal.property_data?.tenants ?? [];
        const allGrossOrPending = (tenants as { lease_type?: string }[]).every(
          (t) => !t.lease_type || t.lease_type === "gross" || t.lease_type === "pending"
        );
        if (!allGrossOrPending) return null;
        return (
          <div className="bg-grove-50 border border-grove-300 rounded-xl px-5 py-3 print:hidden">
            <p className="text-sm text-bark-600">
              <span className="font-semibold text-bark-700">Reimbursement structures have not been configured.</span>{" "}
              All leases are currently modeled on a gross full service basis. Upload a CAM reconciliation or{" "}
              <Link to={`/deals/${dealId}/reimbursements`} className="text-forest-600 underline hover:text-forest-800">
                configure reimbursement structures
              </Link>{" "}
              to update this projection.
            </p>
          </div>
        );
      })()}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div className="flex flex-wrap items-center gap-3">
          {/* Scenario selector */}
          {(() => {
            const withResult = scenarios.filter((s) => s.projection_result);
            if (withResult.length > 1) {
              return (
                <select
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  className="px-3 py-1.5 text-sm bg-white border border-grove-300 rounded-lg text-bark-700"
                >
                  {withResult.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              );
            }
            const current = withResult[0] ?? scenario;
            return current ? (
              <span className="px-3 py-1.5 text-sm font-medium text-bark-700">
                {current.name}
              </span>
            ) : null;
          })()}

          {/* Period toggle */}
          <div className="flex rounded-lg border border-grove-300 overflow-hidden">
            {(["annual", "quarterly", "monthly"] as PeriodMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setPeriodMode(mode)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  periodMode === mode
                    ? "bg-forest-600 text-white"
                    : "bg-white text-bark-500 hover:bg-grove-50"
                }`}
              >
                {mode === "annual" ? "Annual" : mode === "quarterly" ? "Quarterly" : "Monthly"}
              </button>
            ))}
          </div>

          {/* Duration: free numeric input */}
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={1}
              max={30}
              value={durationStr}
              onChange={(e) => handleDurationChange(e.target.value)}
              onBlur={handleDurationBlur}
              className="w-16 px-2 py-1.5 text-sm text-center bg-white border border-grove-300 rounded-lg text-bark-700 tabular-nums"
            />
            <span className="text-xs text-bark-400">Years</span>
          </div>

          {/* View toggle */}
          <div className="flex rounded-lg border border-grove-300 overflow-hidden">
            <button
              onClick={() => setViewMode("gross")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                viewMode === "gross"
                  ? "bg-forest-600 text-white"
                  : "bg-white text-bark-500 hover:bg-grove-50"
              }`}
            >
              Gross ($)
            </button>
            <button
              onClick={() => setViewMode("psf")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                viewMode === "psf"
                  ? "bg-forest-600 text-white"
                  : "bg-white text-bark-500 hover:bg-grove-50"
              }`}
            >
              Per SF ($/SF)
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {recalculating && (
            <span className="flex items-center gap-1.5 text-xs text-bark-400">
              <RefreshCw className="w-3 h-3 animate-spin" />
              Recalculating...
            </span>
          )}
          <button
            onClick={() => setShowEventTimeline(!showEventTimeline)}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all ${
              showEventTimeline
                ? "bg-forest-50 border-forest-300 text-forest-700"
                : "bg-white border-grove-300 text-bark-500 hover:bg-grove-50 hover:shadow-sm"
            }`}
          >
            <Clock className="w-4 h-4" />
            Events
          </button>
          <Link
            to={`/deals/${dealId}/exports`}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-forest-700 border border-forest-800 rounded-lg hover:bg-forest-600 hover:shadow-sm transition-all"
          >
            <Download className="w-4 h-4" />
            Exports
          </Link>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
        </div>
      </div>

      {/* Event Timeline drawer */}
      {showEventTimeline && scenario && dealId && (
        <div className="fixed inset-0 z-50 flex justify-end print:hidden">
          <div
            className="absolute inset-0 bg-black/20"
            onClick={() => setShowEventTimeline(false)}
          />
          <div className="relative w-full max-w-md bg-white shadow-xl border-l border-grove-300 overflow-y-auto animate-slide-in">
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 bg-white border-b border-grove-300">
              <h3 className="text-sm font-semibold text-bark-700">Event Timeline</h3>
              <button
                onClick={() => setShowEventTimeline(false)}
                className="p-1.5 rounded-lg text-bark-400 hover:text-bark-600 hover:bg-grove-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <EventTimeline
                dealId={dealId}
                scenarioId={scenario.id}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main content: sidebar + table */}
      <div className="flex gap-5 print:block">
        {/* Assumptions summary sidebar (read-only) */}
        <div className="w-56 shrink-0 print:hidden">
          <div className="sticky top-4 bg-white rounded-xl border border-grove-300 shadow-sm p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-bark-400">Key Assumptions</span>
              <Link to={`/deals/${dealId}/assumptions`} className="text-[10px] text-forest-600 hover:underline">Edit</Link>
            </div>
            <div className="space-y-1.5 text-[11px]">
              <div className="flex justify-between"><span className="text-bark-400">Vacancy</span><span className="font-medium text-bark-600">{fmtPercent(assumptions.vacancy_rate_pct)}</span></div>
              <div className="flex justify-between"><span className="text-bark-400">Credit Loss</span><span className="font-medium text-bark-600">{fmtPercent(assumptions.credit_loss_pct)}</span></div>
              <div className="flex justify-between"><span className="text-bark-400">Exp. Growth</span><span className="font-medium text-bark-600">{fmtPercent(assumptions.expense_growth_rate_pct)}</span></div>
              <div className="flex justify-between"><span className="text-bark-400">Exit Cap</span><span className="font-medium text-bark-600">{fmtPercent(assumptions.exit_cap_rate, 2)}</span></div>
              <div className="flex justify-between"><span className="text-bark-400">Discount Rate</span><span className="font-medium text-bark-600">{fmtPercent(assumptions.discount_rate, 2)}</span></div>
              <div className="flex justify-between"><span className="text-bark-400">Reserves</span><span className="font-medium text-bark-600">{fmtPSF(assumptions.capital_reserves_psf)}</span></div>
              <div className="flex justify-between"><span className="text-bark-400">Hold Period</span><span className="font-medium text-bark-600">{assumptions.hold_period_years} yrs</span></div>
            </div>
            {scenario?.assumption_overrides && Object.keys(scenario.assumption_overrides).length > 0 && (
              <p className="text-[9px] text-honey-600 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-honey-500" />Year-level overrides active</p>
            )}
          </div>
        </div>

        {/* Pro forma table + return metrics */}
        <div className="flex-1 min-w-0 space-y-4">
          {false && (
            <div className="sticky top-0 z-20 bg-honey-50 border border-honey-300 rounded-lg px-4 py-3 mb-4 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-honey-600" />
                <span className="text-sm font-medium text-honey-800">Assumptions changed</span>
              </div>
              <button
                onClick={() => handleRecalculate(assumptions)}
                className="px-4 py-1.5 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 transition-all"
                title="Update Pro Forma (Cmd+S)"
              >
                Update Pro Forma
                <span className="ml-1.5 text-[10px] text-honey-200 font-normal opacity-80">⌘S</span>
              </button>
            </div>
          )}
          {/* Return Metrics */}
          {(() => {
            const rm = scenario?.projection_result?.return_metrics;
            const pp = deal?.property_data?.purchase_price ?? 0;
            const hasMetrics = rm && (rm.irr_unlevered != null || rm.going_in_cap_rate != null);
            if (!hasMetrics || pp <= 0) {
              if (dismissedPriceBanner) return null;
              return (
                <div className="bg-honey-50 border border-honey-200 rounded-xl px-5 py-3 print:hidden flex items-center justify-between">
                  <p className="text-sm text-bark-500">
                    Enter a purchase price in the Valuation section of the assumptions panel to see return metrics.
                  </p>
                  <button onClick={() => setDismissedPriceBanner(true)} className="text-bark-400 hover:text-bark-600 ml-3 flex-shrink-0" title="Dismiss">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            }
            const hasDebt = ((deal?.property_data?.debt as Record<string, number> | undefined)?.loan_amount ?? 0) > 0;
            const holdPeriod = deal?.property_data?.hold_period_years ?? cfs.length;
            return (
              <div className="bg-white rounded-xl border border-grove-300 shadow-sm p-5">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bark-400 mb-1">Return Metrics</h3>
                <p className="text-[10px] text-bark-300 mb-3">Based on current assumptions</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <MetricCard label="Going-In Cap Rate" value={fmtPercent(deal?.property_data?.going_in_cap_rate || rm.going_in_cap_rate, 2)} />
                  <MetricCard label="Exit Cap Rate" value={fmtPercent(assumptions.exit_cap_rate, 2)} />
                  <MetricCard label="Unlevered IRR" value={fmtPercent(rm.irr_unlevered)} accent />
                  <MetricCard label="Levered IRR" value={hasDebt ? fmtPercent(rm.irr_levered) : (rm.irr_levered != null ? fmtPercent(rm.irr_levered) : "N/A")} accent />
                  <MetricCard label="Equity Multiple" value={fmtMultiple(rm.equity_multiple)} />
                  <MetricCard label="NPV" value={fmtCurrencyCompact(rm.npv)} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
                  <MetricCard label="Purchase Price" value={fmtCurrencyCompact(pp)} sublabel={fmtPSF(pp / tla)} />
                  <MetricCard label="Exit Price" value={fmtCurrencyCompact(rm.exit_price)} sublabel={fmtPSF((rm.exit_price ?? 0) / tla)} />
                  <MetricCard label="Net Exit Proceeds" value={fmtCurrencyCompact(rm.net_exit_proceeds)} />
                  <MetricCard label="Avg Cash-on-Cash" value={rm.avg_cash_on_cash != null ? fmtPercent(rm.avg_cash_on_cash) : "--"} />
                  <MetricCard label="Avg DSCR" value={hasDebt ? (rm.avg_dscr != null ? fmtDecimal(rm.avg_dscr) : "--") : "N/A"} sublabel={hasDebt ? (rm.min_dscr != null ? `Min: ${fmtDecimal(rm.min_dscr)}` : undefined) : "No Debt"} />
                  <MetricCard label="Hold Period" value={`${holdPeriod} Years`} />
                </div>
              </div>
            );
          })()}

          {/* C2: Sensitivity Analysis promotion link */}
          <button
            onClick={() => document.getElementById("sensitivity-section")?.scrollIntoView({ behavior: "smooth" })}
            className="inline-flex items-center gap-2 text-sm text-forest-600 hover:text-forest-800 font-medium transition-colors"
          >
            <TrendingUp className="w-4 h-4" />
            Run Sensitivity Analysis
          </button>

          <div className="bg-white rounded-xl border border-grove-300 overflow-x-auto shadow-sm">
            <table className="w-max min-w-full text-[13px]">
              <thead>
                <tr>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-bark-400 bg-grove-100 border-b-2 border-grove-300 sticky left-0 z-20 min-w-[220px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                    {deal.name || "Property"} Pro Forma
                  </th>
                  {columns.map((col, i) => (
                    <th key={i} className={`px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.06em] border-b-2 border-grove-300 min-w-[130px] ${
                      col.inPlace ? "bg-forest-50 text-forest-600 border-r border-forest-200" : "text-bark-400 bg-grove-100"
                    }`}>
                      {col.label}
                      {col.subLabel && (
                        <span className="block text-[9px] font-normal mt-0.5">{col.subLabel}</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* In-Place data now rendered as the first column — no separate row block needed */}
                {lineItems.filter((item) => {
                  // Hide Debt Service and Cash Flow After Debt Service when no debt
                  const loanAmount = assumptions.loan_amount ?? 0;
                  if (loanAmount <= 0) {
                    if (item.key === "debt_service" || item.key === "cash_flow_after_debt") return false;
                  }
                  return true;
                }).map((item, idx) => {
                  if (item.separator) {
                    return (
                      <tr key={idx}>
                        <td colSpan={columns.length + 1} className="h-px bg-grove-200" />
                      </tr>
                    );
                  }

                  const isPGI = item.expandable && item.key === "potential_gross_income";
                  const isOpex = item.expandableGroup === "opex";
                  const isReimb = item.expandableGroup === "reimbursement";
                  const isExpandableRow = isPGI || isOpex || isReimb;
                  const isExpanded = isPGI ? pgiExpanded : isOpex ? opexExpanded : isReimb ? reimbExpanded : false;
                  const toggleExpand = isPGI
                    ? () => setPgiExpanded(!pgiExpanded)
                    : isOpex
                    ? () => setOpexExpanded(!opexExpanded)
                    : isReimb
                    ? () => setReimbExpanded(!reimbExpanded)
                    : undefined;

                  return (
                    <React.Fragment key={idx}>
                      <tr className={item.bold ? "bg-grove-50" : ""}>
                        <td className={`px-5 py-3 border-b border-grove-200 sticky left-0 z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] ${
                          item.bold ? "font-semibold text-bark-700 bg-grove-50" : "bg-white"
                        } ${item.indent ? "pl-10 text-bark-400" : "text-bark-600"}`}>
                          {isExpandableRow ? (
                            <button
                              onClick={toggleExpand}
                              className="flex items-center gap-1 hover:text-forest-600 transition-colors"
                            >
                              {isExpanded
                                ? <ChevronDown className="w-3.5 h-3.5" />
                                : <ChevronRight className="w-3.5 h-3.5" />}
                              {item.label}
                            </button>
                          ) : item.label}
                        </td>
                        {columns.map((col, i) => {
                          const val = getColumnValue(col, item);
                          const auditTab = item.key ? AUDIT_TAB_MAP[item.key] : undefined;
                          const yearNum = col.cfs[0]?.year;
                          const overrideKey = item.key ? OVERRIDE_KEY_MAP[item.key] : undefined;
                          const hasOverride = overrideKey && scenario?.assumption_overrides?.[overrideKey]?.[String(yearNum)];
                          const isInPlace = !!col.inPlace;
                          return (
                            <td key={i} className={`px-4 py-3 text-right tabular-nums whitespace-nowrap border-b border-grove-200 min-w-[130px] ${cellColor(val, item)}${hasOverride ? " bg-honey-50/60" : ""}${isInPlace ? " bg-forest-50/40 border-r border-forest-200" : ""}`}>
                              {auditTab ? (
                                <span
                                  className="group relative cursor-pointer inline-flex items-center gap-1 hover:text-forest-600 transition-colors"
                                  onClick={(e) => {
                                    if (e.shiftKey) {
                                      setTraceCell({ label: item.label, value: fmtVal(val), year: yearNum || 0 });
                                    } else {
                                      navigate(`/deals/${dealId}/audit?tab=${auditTab}${yearNum ? `&year=${yearNum}` : ""}`);
                                    }
                                  }}
                                >
                                  {fmtVal(val)}
                                  <ClipboardCheck className="w-3 h-3 opacity-0 group-hover:opacity-100 text-forest-500 transition-opacity flex-shrink-0" />
                                  {traceCell && traceCell.label === item.label && traceCell.year === yearNum && (
                                    <SourceTracePopover
                                      dealId={dealId!}
                                      lineLabel={item.label}
                                      value={traceCell.value}
                                      year={traceCell.year}
                                      onClose={() => setTraceCell(null)}
                                    />
                                  )}
                                </span>
                              ) : hasOverride ? (
                                <span className="inline-flex items-center gap-1" title={`Year ${yearNum} override active`}>
                                  <span className="w-1.5 h-1.5 rounded-full bg-honey-500 shrink-0" />
                                  {fmtVal(val)}
                                </span>
                              ) : (
                                fmtVal(val)
                              )}
                            </td>
                          );
                        })}
                      </tr>
                      {/* PGI drill-down rows */}
                      {isPGI && pgiExpanded && periodMode === "annual" && (() => {
                        const firstCf = cfs[0];
                        const tenantDetails = getTenantPGIDetails(firstCf);
                        if (tenantDetails.length === 0) return null;

                        return tenantDetails.map((td, ti) => {
                          // In-Place tenant match: prefer tenant_id, fall back
                          // to name + suite for legacy projection results.
                          const ipTenant = inPlaceTenantDetails.find(
                            (t: any) =>
                              (t.tenant_id != null && td.tenant_id != null && t.tenant_id === td.tenant_id)
                              || (t.tenant_name === td.tenant_name && (t.suite || "") === (td.suite_id || ""))
                          );
                          return (
                          <tr key={`pgi-${ti}`} className="bg-grove-50/50">
                            <td className="pl-10 pr-5 py-2 border-b border-grove-100 sticky left-0 z-10 bg-grove-50 text-[11px] text-bark-400">
                              <span className="font-medium text-bark-500">{td.tenant_name}</span>
                              {td.suite_id && <span className="ml-1 text-bark-300">({td.suite_id})</span>}
                              {ipTenant?.future_start && (
                                <span className="text-[10px] bg-honey-100 text-honey-700 px-1.5 py-0.5 rounded-full ml-1" title={ipTenant.lease_start ? `Lease starts ${ipTenant.lease_start}` : "Lease starts after analysis date"}>Future start</span>
                              )}
                              {td.is_rollover && (
                                <span className="text-[10px] bg-honey-100 text-honey-700 px-1.5 py-0.5 rounded-full ml-1">Rolled</span>
                              )}
                              {td.is_rollover && td.contract_rent_psf != null && (
                                <span className="ml-1.5 text-[10px] text-bark-300">
                                  (was {fmtPSF(td.contract_rent_psf)}, mkt {fmtPSF(td.market_rent_psf ?? 0)}, {fmtPercent(td.renewal_probability ?? 0)} renew)
                                </span>
                              )}
                              {td.rent_step_applied && (
                                <span className="ml-1.5 text-[10px] text-forest-500">{td.rent_step_applied}</span>
                              )}
                            </td>
                            {columns.map((col, ci) => {
                              // In-Place column: use in_place_tenant_details (annualized
                              // contractual rents as of analysis start date)
                              if (col.inPlace && col.inPlaceTenants) {
                                const val = ipTenant ? (ipTenant.annual_rent ?? 0) : 0;
                                const sf = ipTenant?.leased_sf ?? 0;
                                return (
                                  <td key={ci} className="px-4 py-2 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[11px] text-bark-400 bg-forest-50/40 border-r border-forest-200">
                                    {isPSF ? fmtPSF(sf > 0 ? val / sf : 0) : fmtCurrencyAcct(cleanZero(val))}
                                  </td>
                                );
                              }
                              const cf = col.cfs[0];
                              const details = getTenantPGIDetails(cf);
                              const tenantRow = details.find((d) => d.tenant_id === td.tenant_id);
                              const val = tenantRow ? tenantRow.annual_contribution / divisor : 0;
                              return (
                                <td key={ci} className="px-4 py-2 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[11px] text-bark-400">
                                  {isPSF
                                    ? fmtPSF(tenantRow && tenantRow.leased_sf > 0 ? val / tenantRow.leased_sf : 0)
                                    : fmtCurrencyAcct(cleanZero(val))}
                                </td>
                              );
                            })}
                          </tr>
                          );
                        });
                      })()}
                      {/* Opex breakdown rows (two-level: groups → items) */}
                      {isOpex && opexExpanded && (() => {
                        const opexGroups = [
                          { key: "cam", itemsKey: "cam_items", label: "CAM" },
                          { key: "insurance", itemsKey: "insurance_items", label: "Insurance" },
                          { key: "taxes", itemsKey: "taxes_items", label: "Real Estate Taxes" },
                          { key: "management_fee", itemsKey: "management_fee_items", label: "Management Fee" },
                        ] as const;
                        return opexGroups.flatMap((grp) => {
                          const hasValues = columns.some((col) => {
                            const cf0 = col.cfs[0];
                            if (!cf0) return false;
                            const bd = (cf0 as AnnualCashFlow).opex_breakdown;
                            return bd && (bd[grp.key as keyof typeof bd] as number) > 0;
                          });
                          if (!hasValues) return [];
                          const isGroupExpanded = expandedOpexGroups.has(grp.key);
                          const rows: React.ReactNode[] = [];
                          // Group subtotal row (clickable)
                          rows.push(
                            <tr key={`opex-${grp.key}`} className="bg-grove-50/50">
                              <td className="pl-10 pr-5 py-2 border-b border-grove-100 sticky left-0 z-10 bg-grove-50 text-[11px] text-bark-400">
                                <button
                                  onClick={() => toggleOpexGroup(grp.key)}
                                  className="flex items-center gap-1 hover:text-forest-600 transition-colors"
                                >
                                  {isGroupExpanded
                                    ? <ChevronDown className="w-3 h-3" />
                                    : <ChevronRight className="w-3 h-3" />}
                                  <span className="font-medium text-bark-500">{grp.label}</span>
                                </button>
                              </td>
                              {columns.map((col, ci) => {
                                if (col.inPlace) {
                                  // In-Place column: no per-category opex breakdown available
                                  return (
                                    <td key={ci} className="px-4 py-2 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[11px] text-bark-400 bg-forest-50/40 border-r border-forest-200">
                                      --
                                    </td>
                                  );
                                }
                                const cf0 = col.cfs[0];
                                const bd = cf0 ? (cf0 as AnnualCashFlow).opex_breakdown : undefined;
                                const val = bd ? -(bd[grp.key as keyof typeof bd] as number) / divisor : 0;
                                return (
                                  <td key={ci} className="px-4 py-2 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[11px] text-bark-400">
                                    {fmtVal(val)}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                          // Expanded line items
                          if (isGroupExpanded) {
                            const firstBd = (cfs[0] as AnnualCashFlow).opex_breakdown;
                            const items = firstBd ? (firstBd[grp.itemsKey as keyof typeof firstBd] as { name: string; amount: number }[]) : [];
                            items.forEach((lineItem, li) => {
                              rows.push(
                                <tr key={`opex-${grp.key}-${li}`} className="bg-grove-50/30">
                                  <td className="pl-16 pr-5 py-1.5 border-b border-grove-100 sticky left-0 z-10 bg-white text-[10px] text-bark-300">
                                    {lineItem.name}
                                  </td>
                                  {columns.map((col, ci) => {
                                    if (col.inPlace) {
                                      return (
                                        <td key={ci} className="px-4 py-1.5 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[10px] text-bark-300 bg-forest-50/40 border-r border-forest-200">
                                          --
                                        </td>
                                      );
                                    }
                                    const cf0 = col.cfs[0];
                                    const bd = cf0 ? (cf0 as AnnualCashFlow).opex_breakdown : undefined;
                                    const colItems = bd ? (bd[grp.itemsKey as keyof typeof bd] as { name: string; amount: number }[]) : [];
                                    const match = colItems.find((it) => it.name === lineItem.name);
                                    const val = match ? -match.amount / divisor : 0;
                                    return (
                                      <td key={ci} className="px-4 py-1.5 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[10px] text-bark-300">
                                        {fmtVal(val)}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            });
                          }
                          return rows;
                        });
                      })()}
                      {/* Reimbursement breakdown — 3-level hierarchy: Pool → Tenant → Line Items */}
                      {isReimb && reimbExpanded && (() => {
                        type PoolKey = "cam_reimbursement" | "tax_reimbursement" | "insurance_reimbursement" | "management_fee_reimbursement";
                        type LineItemsKey = "cam_line_items" | "tax_line_items" | "insurance_line_items";
                        const reimbPools: { key: PoolKey; poolId: string; label: string; lineItemsKey?: LineItemsKey }[] = [
                          { key: "cam_reimbursement", poolId: "cam", label: "CAM Reimbursement", lineItemsKey: "cam_line_items" },
                          { key: "tax_reimbursement", poolId: "tax", label: "Tax Reimbursement", lineItemsKey: "tax_line_items" },
                          { key: "insurance_reimbursement", poolId: "insurance", label: "Insurance Reimbursement", lineItemsKey: "insurance_line_items" },
                          { key: "management_fee_reimbursement", poolId: "mgmt", label: "Management Fee Reimb." },
                        ];

                        const getTenantDetails = (cf: AnnualCashFlow | undefined) => {
                          if (!cf) return [] as TenantReimbursementSummary[];
                          return ((cf as unknown as Record<string, unknown>).tenant_reimbursement_details || []) as TenantReimbursementSummary[];
                        };

                        const getPoolVal = (cf: AnnualCashFlow | undefined, key: PoolKey): number => {
                          if (!cf) return 0;
                          const rb = cf.reimbursement_breakdown;
                          if (rb && (rb as unknown as Record<string, number>)[key] != null) return (rb as unknown as Record<string, number>)[key];
                          const trd = getTenantDetails(cf);
                          return trd.reduce((s, t) => s + ((t as unknown as Record<string, number>)[key] || 0), 0);
                        };

                        // In-Place column: per-pool fields on in_place_tenant_details
                        const IP_POOL_FIELD: Record<string, string> = {
                          cam: "cam_reimbursement",
                          tax: "tax_reimbursement",
                          insurance: "insurance_reimbursement",
                          mgmt: "admin_fee",
                        };
                        const getInPlacePoolSum = (col: typeof columns[0], poolId: string): number => {
                          if (!col.inPlaceTenants) return 0;
                          const field = IP_POOL_FIELD[poolId];
                          return (col.inPlaceTenants as any[]).reduce((s: number, t: any) => s + (t[field] ?? 0), 0);
                        };
                        const findInPlaceTenant = (col: typeof columns[0], trd: TenantReimbursementSummary) => {
                          if (!col.inPlaceTenants) return undefined;
                          return (col.inPlaceTenants as any[]).find(
                            (t: any) =>
                              (t.tenant_id != null && trd.tenant_id != null && t.tenant_id === trd.tenant_id)
                              || t.tenant_name === trd.tenant_name
                          );
                        };

                        const rows: React.ReactNode[] = [];
                        const firstCfTenants = getTenantDetails(cfs[0]);

                        for (const pool of reimbPools) {
                          // Check if any column (year or In-Place) has data for this pool
                          const hasValues = columns.some((col) =>
                            col.inPlace
                              ? getInPlacePoolSum(col, pool.poolId) > 0.5
                              : getPoolVal(col.cfs[0], pool.key) > 0.5
                          );
                          if (!hasValues) continue;

                          const isPoolExpanded = expandedReimbPools.has(pool.poolId);

                          // Level 1: Pool subtotal row (clickable)
                          rows.push(
                            <tr
                              key={`reimb-${pool.poolId}`}
                              className="bg-grove-50/50 cursor-pointer hover:bg-grove-100/60 transition-colors"
                              onClick={() => toggleReimbPool(pool.poolId)}
                            >
                              <td className="pl-10 pr-5 py-2 border-b border-grove-100 sticky left-0 z-10 bg-grove-50 text-[11px]">
                                <span className="inline-flex items-center gap-1.5">
                                  <ChevronRight className={`w-3 h-3 text-bark-400 transition-transform ${isPoolExpanded ? "rotate-90" : ""}`} />
                                  <span className="font-medium text-bark-500">{pool.label}</span>
                                </span>
                              </td>
                              {columns.map((col, ci) => (
                                <td key={ci} className={`px-4 py-2 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[11px] text-bark-400${col.inPlace ? " bg-forest-50/40 border-r border-forest-200" : ""}`}>
                                  {col.inPlace
                                    ? fmtVal(getInPlacePoolSum(col, pool.poolId))
                                    : fmtVal(getPoolVal(col.cfs[0], pool.key) / divisor)}
                                </td>
                              ))}
                            </tr>
                          );

                          // Level 2: Per-tenant rows within pool
                          if (isPoolExpanded && firstCfTenants.length > 0) {
                            for (const trd of firstCfTenants) {
                              const hasTenantPool = columns.some((col) => {
                                if (col.inPlace) {
                                  const ip = findInPlaceTenant(col, trd);
                                  return ip && ((ip[IP_POOL_FIELD[pool.poolId]] as number) || 0) > 0.5;
                                }
                                const match = getTenantDetails(col.cfs[0]).find((d) => d.tenant_id === trd.tenant_id);
                                return match && ((match as unknown as Record<string, number>)[pool.key] || 0) > 0.5;
                              });
                              if (!hasTenantPool) continue;

                              const tenantKey = `${pool.poolId}__${trd.tenant_id}`;
                              const isTenantExpanded = expandedReimbTenants.has(tenantKey);
                              const hasLineItems = pool.lineItemsKey && trd[pool.lineItemsKey] && trd[pool.lineItemsKey].length > 0;

                              rows.push(
                                <tr
                                  key={`reimb-t-${tenantKey}`}
                                  className={`bg-grove-50/30 ${hasLineItems ? "cursor-pointer hover:bg-grove-100/40" : ""} transition-colors`}
                                  onClick={hasLineItems ? () => toggleReimbTenant(tenantKey) : undefined}
                                >
                                  <td className="pl-14 pr-5 py-1.5 border-b border-grove-100 sticky left-0 z-10 bg-grove-50/30 text-[10px] text-bark-400">
                                    <span className="inline-flex items-center gap-1.5">
                                      {hasLineItems && (
                                        <ChevronRight className={`w-2.5 h-2.5 text-bark-300 transition-transform ${isTenantExpanded ? "rotate-90" : ""}`} />
                                      )}
                                      <span className="font-medium text-bark-500">{trd.tenant_name}</span>
                                      {trd.leased_sf > 0 && <span className="text-bark-300">({fmtSF(trd.leased_sf)})</span>}
                                    </span>
                                  </td>
                                  {columns.map((col, ci) => {
                                    if (col.inPlace) {
                                      const ip = findInPlaceTenant(col, trd);
                                      const ipVal = ip ? ((ip[IP_POOL_FIELD[pool.poolId]] as number) || 0) : 0;
                                      return (
                                        <td key={ci} className="px-4 py-1.5 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[10px] text-bark-300 bg-forest-50/40 border-r border-forest-200">
                                          {fmtValTenant(ipVal, trd.leased_sf)}
                                        </td>
                                      );
                                    }
                                    const match = getTenantDetails(col.cfs[0]).find((d) => d.tenant_id === trd.tenant_id);
                                    const val = match ? ((match as unknown as Record<string, number>)[pool.key] || 0) / divisor : 0;
                                    return (
                                      <td key={ci} className="px-4 py-1.5 text-right tabular-nums whitespace-nowrap border-b border-grove-100 text-[10px] text-bark-300">
                                        {fmtValTenant(val, trd.leased_sf)}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );

                              // Level 3: Line items within tenant-pool
                              if (isTenantExpanded && hasLineItems && pool.lineItemsKey) {
                                for (const li of trd[pool.lineItemsKey]) {
                                  if (li.amount < 0.5 && li.expense_amount < 0.5) continue;
                                  rows.push(
                                    <tr key={`reimb-li-${tenantKey}-${li.category}`} className="bg-grove-50/20">
                                      <td className="pl-20 pr-5 py-1 border-b border-grove-100/60 sticky left-0 z-10 bg-grove-50/20 text-[10px] text-bark-300">
                                        {li.category}
                                      </td>
                                      {columns.map((col, ci) => {
                                        if (col.inPlace) {
                                          return (
                                            <td key={ci} className="px-4 py-1 text-right tabular-nums whitespace-nowrap border-b border-grove-100/60 text-[10px] text-bark-300/70 bg-forest-50/40 border-r border-forest-200">
                                              --
                                            </td>
                                          );
                                        }
                                        const match = getTenantDetails(col.cfs[0]).find((d) => d.tenant_id === trd.tenant_id);
                                        const items = match && pool.lineItemsKey ? (match[pool.lineItemsKey] || []) : [];
                                        const item = items.find((x: { category: string }) => x.category === li.category);
                                        const val = item ? item.amount / divisor : 0;
                                        return (
                                          <td key={ci} className="px-4 py-1 text-right tabular-nums whitespace-nowrap border-b border-grove-100/60 text-[10px] text-bark-300/70">
                                            {fmtValTenant(val, trd.leased_sf)}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                }
                              }
                            }
                          }
                        }
                        return rows;
                      })()}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Sensitivity Analysis — inside flex-1 panel, below the table */}
          {scenario?.projection_result && selectedId && (
            <div id="sensitivity-section" className="mt-6 print:hidden">
              <SensitivityAnalysis
                dealId={dealId!}
                scenarioId={selectedId}
                currentAssumptions={{
                  exit_cap_rate: assumptions.exit_cap_rate,
                  purchase_price: assumptions.purchase_price || (deal?.property_data?.purchase_price as number) || 0,
                  general_vacancy_pct: assumptions.vacancy_rate_pct,
                  expense_growth_rate_pct: assumptions.expense_growth_rate_pct,
                  market_rent_growth_pct_annual: assumptions.market_rent_growth_pct_annual,
                  discount_rate: assumptions.discount_rate,
                }}
              />
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
